import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config';

const AdminPage = () => {
  const [events, setEvents] = useState([]);
  const [defaultEventId, setDefaultEventId] = useState('');
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configStatus, setConfigStatus] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${CONFIG.apiBaseUrl}/admin/events`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${CONFIG.apiBaseUrl}/admin/global-config`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([eventsData, configData]) => {
      setEvents(eventsData.events || []);
      setDefaultEventId(configData.default_event_id || '');
    }).catch(console.error).finally(() => setConfigLoading(false));
  }, []);

  const saveGlobalConfig = async () => {
    setConfigSaving(true);
    setConfigStatus(null);
    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/global-config`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_event_id: defaultEventId || null }),
      });
      if (!resp.ok) throw new Error('Failed to save');
      setConfigStatus('saved');
      setTimeout(() => setConfigStatus(null), 3000);
    } catch (err) {
      setConfigStatus('error');
    } finally {
      setConfigSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <h2 className="page-title">Admin Dashboard</h2>

      <div className="admin-content">
        <div className="settings-section">
          <h3>Global Settings</h3>
          {configLoading ? (
            <div className="loading-message">Loading...</div>
          ) : (
            <div className="form-group" style={{ maxWidth: '400px' }}>
              <label className="form-label">Default Event (keyword fallback)</label>
              <select
                className="form-input"
                value={defaultEventId}
                onChange={(e) => setDefaultEventId(e.target.value)}
              >
                <option value="">— None —</option>
                {events.map(e => (
                  <option key={e.event_id} value={e.event_id}>{e.event_name} ({e.event_id})</option>
                ))}
              </select>
              <div className="input-hint">
                When an unknown sender texts a keyword (e.g. "START") and their destination phone number doesn't match a specific event, they'll be subscribed to this event.
              </div>
              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button className="btn btn-primary" onClick={saveGlobalConfig} disabled={configSaving}>
                  {configSaving ? 'Saving...' : 'Save'}
                </button>
                {configStatus === 'saved' && <span style={{ color: '#16a34a', fontSize: '0.9em' }}>Saved</span>}
                {configStatus === 'error' && <span style={{ color: '#dc3545', fontSize: '0.9em' }}>Failed to save</span>}
              </div>
            </div>
          )}
        </div>

        <div className="admin-tools-box">
          <h3>Admin Tools</h3>
          <div className="admin-tools-grid">
            <a href="/admin/inbound-messages" className="admin-tool-card" onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', '/admin/inbound-messages');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}>
              <div className="tool-icon">💬</div>
              <div className="tool-name">Messages Log</div>
              <div className="tool-description">View inbound SMS and WhatsApp messages, Lex bot responses</div>
            </a>

            <a href="/admin/events" className="admin-tool-card" onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', '/admin/events');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}>
              <div className="tool-icon">📋</div>
              <div className="tool-name">Event Management</div>
              <div className="tool-description">Configure events, phone numbers, and template variables</div>
            </a>

            <a href="/admin/templates" className="admin-tool-card" onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', '/admin/templates');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}>
              <div className="tool-icon">📝</div>
              <div className="tool-name">Message Templates</div>
              <div className="tool-description">Create and manage reusable message templates for SMS and WhatsApp</div>
            </a>

            <a href="/admin/self-registration" className="admin-tool-card" onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', '/admin/self-registration');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}>
              <div className="tool-icon">🔗</div>
              <div className="tool-name">Self-Registration</div>
              <div className="tool-description">Generate registration links and QR codes for guests</div>
            </a>
            <a href="/admin/media" className="admin-tool-card" onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', '/admin/media');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}>
              <div className="tool-icon">🖼️</div>
              <div className="tool-name">Media</div>
              <div className="tool-description">View and download media files received from guests</div>
            </a>
          </div>
        </div>

        <div className="admin-notice">
          <p>This is an admin-only page. Only users in the "admin" group can access this area.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
